/**
 * Affiliate API Client
 * API functions for affiliate portal
 */

import { apiClient } from './client';

export const affiliateApi = {
  /**
   * Get affiliate dashboard data
   */
  getDashboard: async () => {
    const response = await apiClient.get('/affiliate/dashboard');
    return response.data;
  },

  /**
   * Get monthly statistics for charts
   */
  getMonthlyStats: async (months: number = 12) => {
    const response = await apiClient.get(`/affiliate/dashboard/stats/monthly?months=${months}`);
    return response.data;
  },

  /**
   * Get referral links
   */
  getReferralLinks: async () => {
    const response = await apiClient.get('/affiliate/dashboard/links');
    return response.data;
  },

  /**
   * Generate custom referral link
   */
  generateCustomLink: async (targetUrl: string) => {
    const response = await apiClient.post('/affiliate/dashboard/links/generate', {
      targetUrl,
    });
    return response.data;
  },

  /**
   * Get affiliate profile
   */
  getProfile: async () => {
    const response = await apiClient.get('/affiliate/profile');
    return response.data;
  },

  /**
   * Update affiliate profile
   */
  updateProfile: async (data: {
    firstName?: string;
    lastName?: string;
    email?: string;
    companyName?: string;
  }) => {
    const response = await apiClient.put('/affiliate/profile', data);
    return response.data;
  },

  /**
   * Update payment method
   */
  updatePaymentMethod: async (data: {
    paymentMethod: string;
    paymentEmail?: string;
    paymentDetails?: any;
  }) => {
    const response = await apiClient.put('/affiliate/profile/payment-method', data);
    return response.data;
  },

  /**
   * Get referrals list
   */
  getReferrals: async (params?: {
    page?: number;
    limit?: number;
    converted?: '0' | '1';
  }) => {
    const query = new URLSearchParams(params as any).toString();
    const response = await apiClient.get(`/affiliate/referrals?${query}`);
    return response.data;
  },

  /**
   * Get referral by ID
   */
  getReferralById: async (id: number) => {
    const response = await apiClient.get(`/affiliate/referrals/${id}`);
    return response.data;
  },

  /**
   * Get referral summary stats
   */
  getReferralStats: async () => {
    const response = await apiClient.get('/affiliate/referrals/stats/summary');
    return response.data;
  },

  /**
   * Get commissions list
   */
  getCommissions: async (params?: {
    page?: number;
    limit?: number;
    status?: string;
  }) => {
    const query = new URLSearchParams(params as any).toString();
    const response = await apiClient.get(`/affiliate/commissions?${query}`);
    return response.data;
  },

  /**
   * Get commission by ID
   */
  getCommissionById: async (id: number) => {
    const response = await apiClient.get(`/affiliate/commissions/${id}`);
    return response.data;
  },

  /**
   * Get commission summary stats
   */
  getCommissionStats: async () => {
    const response = await apiClient.get('/affiliate/commissions/stats/summary');
    return response.data;
  },

  /**
   * Get earnings breakdown
   */
  getEarningsBreakdown: async () => {
    const response = await apiClient.get('/affiliate/commissions/stats/earnings-breakdown');
    return response.data;
  },

  /**
   * Get commissions available for payout
   */
  getAvailableForPayout: async () => {
    const response = await apiClient.get('/affiliate/commissions/available-for-payout');
    return response.data;
  },

  /**
   * Get payouts list
   */
  getPayouts: async (params?: {
    page?: number;
    limit?: number;
    status?: string;
  }) => {
    const query = new URLSearchParams(params as any).toString();
    const response = await apiClient.get(`/affiliate/payouts?${query}`);
    return response.data;
  },

  /**
   * Get payout by ID
   */
  getPayoutById: async (id: number) => {
    const response = await apiClient.get(`/affiliate/payouts/${id}`);
    return response.data;
  },

  /**
   * Get payout summary stats
   */
  getPayoutStats: async () => {
    const response = await apiClient.get('/affiliate/payouts/stats/summary');
    return response.data;
  },

  /**
   * Get marketing materials
   */
  getMaterials: async () => {
    const response = await apiClient.get('/affiliate/materials');
    return response.data;
  },

  /**
   * Get banners only
   */
  getBanners: async () => {
    const response = await apiClient.get('/affiliate/materials/banners');
    return response.data;
  },

  /**
   * Get text links only
   */
  getTextLinks: async () => {
    const response = await apiClient.get('/affiliate/materials/text-links');
    return response.data;
  },

  /**
   * Request a payout
   */
  requestPayout: async (data: {
    amount?: number;
    paymentMethod?: string;
    paymentEmail?: string;
    paymentDetails?: string;
  }) => {
    const response = await apiClient.post('/affiliate/payouts/request', data);
    return response.data;
  },

  /**
   * Get performance report
   */
  getPerformanceReport: async (queryString?: string) => {
    const response = await apiClient.get(`/affiliate/reports/performance${queryString ? `?${queryString}` : ''}`);
    return response.data;
  },

  /**
   * Get referrals timeline
   */
  getReferralsTimeline: async (months: number = 12) => {
    const response = await apiClient.get(`/affiliate/reports/referrals-timeline?months=${months}`);
    return response.data;
  },

  /**
   * Get commissions timeline
   */
  getCommissionsTimeline: async (months: number = 12) => {
    const response = await apiClient.get(`/affiliate/reports/commission-timeline?months=${months}`);
    return response.data;
  },
};
